import { fetchAuthSession } from 'aws-amplify/auth';
import { userService } from './userService';
import { TemplateType } from '../types/templateTypes';

// API Configuration
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Auto-select API by environment
const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
    if (isLocalhost || isDevSite) return DEV_API_BASE;
  }
  return PROD_API_BASE;
})();

export interface Resource {
  id: string;
  title: string;
  content: any; // TipTap JSON content
  plain_text: string;
  category: string;
  description?: string;
  tags?: string[];
  version: number;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  resource_type: 'manual';
  associated_projects?: Array<{
    project_uuid: string;
    project_name: string;
    project_color?: string;
  }>;
}

export interface CreateResourceRequest {
  title: string;
  content: any; // TipTap JSON content
  category?: string;
  description?: string;
  tags?: string[];
  project_id?: string; // Optional project to auto-associate
}

export interface UpdateResourceRequest {
  title?: string;
  content?: any; // TipTap JSON content
  category?: string;
  description?: string;
  tags?: string[];
}

class ResourcesService {
  private async makeRequest(url: string, options: RequestInit = {}) {
    try {
      const session = await fetchAuthSession();
      const jwtToken = session.tokens?.idToken?.toString();

      if (!jwtToken) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
          url: response.url
        });
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  async getResources(): Promise<Resource[]> {
    try {
      console.log('[ResourcesService] Fetching resources from:', `${API_BASE_URL}/resources`);
      const response = await this.makeRequest(`${API_BASE_URL}/resources`);
      console.log('[ResourcesService] Raw response:', response);
      
      if (response.resources && Array.isArray(response.resources)) {
        console.log('[ResourcesService] Found resources in response.resources, count:', response.resources.length);
        // Debug the first resource to see content structure
        if (response.resources.length > 0) {
          console.log('[ResourcesService] First resource content debug:', {
            id: response.resources[0].id,
            title: response.resources[0].title,
            contentType: typeof response.resources[0].content,
            contentPreview: response.resources[0].content ? JSON.stringify(response.resources[0].content).substring(0, 200) + '...' : 'no content'
          });
        }
        return response.resources;
      }
      
      const fallbackData = response.data || response || [];
      console.log('[ResourcesService] Using fallback data, count:', Array.isArray(fallbackData) ? fallbackData.length : 'not an array');
      return Array.isArray(fallbackData) ? fallbackData : [];
    } catch (error) {
      console.error('[ResourcesService] Error fetching resources:', error);
      throw new Error(`Failed to fetch resources: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getResource(resourceId: string): Promise<Resource> {
    try {
      const data = await this.makeRequest(`${API_BASE_URL}/resources/${resourceId}`);
      return data;
    } catch (error) {
      console.error('Error fetching resource:', error);
      throw new Error('Failed to fetch resource');
    }
  }

  async createResource(data: CreateResourceRequest): Promise<Resource> {
    try {
      const { project_id, ...resourceData } = data;
      
      console.log('🔍 [ResourcesService] Creating resource with data:', {
        ...resourceData,
        contentType: typeof resourceData.content,
        contentPreview: JSON.stringify(resourceData.content).substring(0, 200) + '...'
      });
      
      // Create the resource first
      const response = await this.makeRequest(`${API_BASE_URL}/resources`, {
        method: 'POST',
        body: JSON.stringify(resourceData),
      });
      
      // If project_id is provided, automatically associate the resource with the project
      if (project_id && response.id) {
        try {
          const { addKnowledgeResource } = await import('./projectsService');
          await addKnowledgeResource(project_id, response.id, response.title);
          console.log('✅ [ResourcesService] Resource automatically associated with project:', project_id);
        } catch (associationError) {
          console.error('❌ [ResourcesService] Failed to associate resource with project:', associationError);
          // Don't fail the entire operation if association fails
          // The resource was still created successfully
        }
      }
      
      return response;
    } catch (error) {
      console.error('Error creating resource:', error);
      throw new Error('Failed to create resource');
    }
  }

  async createResourceForProject(projectId: string, data: Omit<CreateResourceRequest, 'project_id'>): Promise<Resource> {
    return this.createResource({ ...data, project_id: projectId });
  }

  async updateResource(resourceId: string, data: UpdateResourceRequest): Promise<Resource> {
    try {
      console.log('🔍 [ResourcesService] Updating resource:', resourceId, 'with data:', {
        ...data,
        contentType: typeof data.content,
        contentPreview: data.content ? JSON.stringify(data.content).substring(0, 200) + '...' : 'no content'
      });
      
      const response = await this.makeRequest(`${API_BASE_URL}/resources/${resourceId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      
      console.log('✅ [ResourcesService] Update response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ResourcesService] Error updating resource:', error);
      throw new Error('Failed to update resource');
    }
  }

  async deleteResource(resourceId: string): Promise<void> {
    try {
      await this.makeRequest(`${API_BASE_URL}/resources/${resourceId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw new Error('Failed to delete resource');
    }
  }

  async searchResources(query: string): Promise<Resource[]> {
    try {
      // For now, just filter existing resources by title/content
      // In the future, this could call a dedicated search endpoint
      const allResources = await this.getResources();
      
      const lowercaseQuery = query.toLowerCase();
      return allResources.filter(resource => 
        resource.title.toLowerCase().includes(lowercaseQuery) ||
        resource.plain_text.toLowerCase().includes(lowercaseQuery) ||
        resource.description?.toLowerCase().includes(lowercaseQuery)
      );
    } catch (error) {
      console.error('Error searching resources:', error);
      throw new Error('Failed to search resources');
    }
  }
}

export const resourcesService = new ResourcesService();